/**
 * The one conversion between an **instant** the api stores and the **wall clock**
 * a native `<input type="datetime-local">` speaks.
 *
 * ## Why this exists rather than `new Date(value).toISOString()`
 *
 * That one-liner is correct in a browser and untestable anywhere else: it reads
 * the machine's zone, so a spec asserting a real conversion is green on a UTC CI
 * runner whatever the code does. `apps/mobile` paid for that lesson three
 * separate ways (P13c2a — the offset sign, the offset read at the wrong instant,
 * and a spring-forward wall clock paired with the resolved offset naming a
 * *third* time), and had to pin `process.env.TZ` in a jest global setup because a
 * zone written from inside a spec never reaches `Date`.
 *
 * So the **zone is a parameter**, defaulting to the browser's own. Every function
 * here is a pure function of `(value, timeZone)`, the spec walks Riyadh,
 * Kathmandu (+05:45) and Berlin (both transitions) without touching the ambient
 * zone at all, and the admin suite needs no global setup that could silently stop
 * applying.
 *
 * Its mobile counterpart is `apps/mobile/src/shared/utils/local-instant` — the
 * same knowledge, deliberately **not** shared: `shared/` is per app (U2), the
 * shapes differ (wheels there, one string here), and a cross-app import is
 * forbidden. What is shared is the reasoning, and it is written down in both.
 */

/** `YYYY-MM-DDTHH:mm` with optional seconds — exactly what the native input emits. */
const LOCAL_INPUT_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/u;

/** A full instant: a day, a `T`, a time, and a zone designator that is PRESENT. */
const ISO_INSTANT_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?(?:[Zz]|[+-]\d{2}:\d{2})$/u;

const MS_PER_MINUTE = 60_000;
const MS_PER_DAY = 86_400_000;
const HOURS_PER_DAY = 24;
const MINUTES_PER_HOUR = 60;

/**
 * Whether those three numbers name a day that exists.
 *
 * Matching the pattern is **not** being a date, and neither `Date.parse` nor
 * `Date.UTC` will say so: `Date.parse('2026-02-31T09:00:00.000Z')` does not
 * answer Invalid Date, it rolls forward to **3 March**. A string that names no
 * day would otherwise become an appointment three days away from the one anybody
 * typed — which is exactly the class of error this module exists to stop, and it
 * is the api's `2026-02-31` case the spec caught here.
 */
function isRealCalendarDay(year: number, month: number, day: number): boolean {
  const rolled = new Date(Date.UTC(year, month - 1, day));

  return (
    rolled.getUTCFullYear() === year &&
    rolled.getUTCMonth() === month - 1 &&
    rolled.getUTCDate() === day
  );
}

/**
 * `hourCycle: 'h23'` is load-bearing, not a preference.
 *
 * `hour: '2-digit'` on its own resolves to a 12-hour clock in many locales and —
 * worse — answers `"24"` for midnight under `h24`, which
 * `<input type="datetime-local">` rejects outright: the field renders **empty**
 * and the operator sees no appointment at all. `en-US` is a formatting locale,
 * never a display one; nothing here is shown to anybody.
 */
const PART_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
};

const formatters = new Map<string, Intl.DateTimeFormat>();

/** Memoized — a table of appointments would otherwise build one per cell. */
function formatterFor(timeZone: string): Intl.DateTimeFormat {
  const cached = formatters.get(timeZone);
  if (cached !== undefined) return cached;

  const created = new Intl.DateTimeFormat('en-US', { ...PART_OPTIONS, timeZone });
  formatters.set(timeZone, created);
  return created;
}

export interface WallClock {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

/** The zone's wall clock at an instant, as numbers. */
function wallClockAt(utcMs: number, timeZone: string): WallClock {
  const parts = formatterFor(timeZone).formatToParts(new Date(utcMs));
  const read = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((part) => part.type === type)?.value ?? '0');

  return {
    year: read('year'),
    month: read('month'),
    day: read('day'),
    // `h23` gives 00–23, so no midnight special case is needed here — see
    // PART_OPTIONS for what happens without it.
    hour: read('hour'),
    minute: read('minute'),
    second: read('second'),
  };
}

/**
 * The zone's browser-resolved name.
 *
 * Read at the call rather than at module scope: a spec, a lab and a screenshot
 * harness may all run under different zones in one process, and a value captured
 * at import time would be whichever ran first.
 */
export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * The calendar day and clock a zone shows at an instant — the parts every
 * brand-pattern formatter renders from (P28), or `null` for anything that is
 * not an instant.
 *
 * The weekday is derived from the wall-clock DAY, not from the instant: it is
 * the day the zone names, which can differ from UTC's on either side of midnight.
 */
export function wallClockOf(
  iso: string | null | undefined,
  timeZone: string = browserTimeZone(),
): (WallClock & { weekday: number }) | null {
  if (iso === null || iso === undefined || iso === '') return null;

  const utcMs = Date.parse(iso);
  if (Number.isNaN(utcMs)) return null;

  const wall = wallClockAt(utcMs, timeZone);
  const weekday = new Date(Date.UTC(wall.year, wall.month - 1, wall.day)).getUTCDay();

  return { ...wall, weekday };
}

/**
 * How far **ahead of UTC** the zone is at that instant, in minutes.
 *
 * Positive ahead — the opposite sign to `Date.prototype.getTimezoneOffset()`,
 * which answers `-180` for UTC+3 and is the single most expensive sign error in
 * this codebase's history. Nothing here calls it: the offset is derived by
 * reading the zone's own wall clock and re-reading it as if it were UTC, which
 * has no sign to get wrong.
 *
 * Read **at the instant**, never at "now": half of Europe changes offset in
 * October, so an appointment made in September for December is an hour out if
 * today's offset is used.
 */
export function zoneOffsetMinutes(utcMs: number, timeZone: string = browserTimeZone()): number {
  const wall = wallClockAt(utcMs, timeZone);
  const asIfUtc = Date.UTC(
    wall.year,
    wall.month - 1,
    wall.day,
    wall.hour,
    wall.minute,
    wall.second,
  );

  return Math.round((asIfUtc - utcMs) / MS_PER_MINUTE);
}

/**
 * An instant as the wall clock a `datetime-local` input renders — or `''`.
 *
 * The empty string rather than `null`, because that is what keeps the native
 * input **controlled**: handing it `null` mid-life gives control back to the DOM
 * and React warns (the same reason `DatePicker` writes `value ?? ''`).
 *
 * Anything that is not an instant answers `''` too: an unparseable value is not
 * a time, and printing a partial one would let a reviewer confirm a slot the
 * platform never understood.
 */
export function toLocalInput(iso: string | null, timeZone: string = browserTimeZone()): string {
  if (iso === null || iso === '') return '';

  const match = ISO_INSTANT_PATTERN.exec(iso);
  if (match === null) return '';

  const [, year = '', month = '', day = ''] = match;
  if (!isRealCalendarDay(Number(year), Number(month), Number(day))) return '';

  const utcMs = Date.parse(iso);
  if (Number.isNaN(utcMs)) return '';

  const wall = wallClockAt(utcMs, timeZone);
  const pad = (value: number, width = 2): string => String(value).padStart(width, '0');

  return `${pad(wall.year, 4)}-${pad(wall.month)}-${pad(wall.day)}T${pad(wall.hour)}:${pad(
    wall.minute,
  )}`;
}

/**
 * The wall clock a reviewer typed, as the instant the api stores — or `null`.
 *
 * ## Candidates, then the EARLIER one
 *
 * The wall clock is first read **as if it were UTC**, giving a number that is
 * wrong by exactly the zone's offset. Subtracting "the offset" is where a naive
 * implementation goes wrong, because across a transition there are **two** of
 * them and neither is knowable before the answer is. So both are taken — the
 * offset a day earlier and the offset a day later — each is subtracted to
 * produce a candidate instant, and a candidate is kept only if the zone actually
 * renders it back as the wall clock that was asked for.
 *
 * When both survive, the wall clock is the **autumn hour that happens twice**,
 * and the **earlier** is chosen. That is not arbitrary preference: `apps/mobile`
 * resolves the same ambiguity through `new Date(y, m, d, h, mi)`, which the
 * platform defines as the first occurrence, and a member proposing a slot on the
 * phone must not name an instant an hour away from the one staff confirm in the
 * portal.
 *
 * When **neither** survives, the wall clock is inside a spring-forward gap and
 * names no instant at all. Nothing can make it name one, so the promise narrows
 * to what the spec pins: the answer is a real instant, and asking twice gives the
 * same one.
 *
 * Seconds are always `00`. The control offers no finer grain, and a value
 * carrying whatever second the operator happened to confirm on would make two
 * identical-looking appointments different values.
 */
export function fromLocalInput(value: string, timeZone: string = browserTimeZone()): string | null {
  const match = LOCAL_INPUT_PATTERN.exec(value);
  if (match === null) return null;

  const [, year = '', month = '', day = '', hour = '', minute = ''] = match;

  if (!isRealCalendarDay(Number(year), Number(month), Number(day))) return null;
  if (Number(hour) >= HOURS_PER_DAY || Number(minute) >= MINUTES_PER_HOUR) return null;

  const wallAsUtc = Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
  );

  const candidates = [wallAsUtc - MS_PER_DAY, wallAsUtc + MS_PER_DAY]
    .map((probe) => wallAsUtc - zoneOffsetMinutes(probe, timeZone) * MS_PER_MINUTE)
    // Kept only if the zone renders it back as the wall clock asked for — which
    // is what excludes the candidate that fell on the wrong side of a change.
    .filter(
      (candidate) =>
        wallAsUtc - zoneOffsetMinutes(candidate, timeZone) * MS_PER_MINUTE === candidate,
    );

  if (candidates.length > 0) {
    return new Date(Math.min(...candidates)).toISOString();
  }

  // The gap. Two passes converge on a real instant an hour past what was typed,
  // which is what the zone itself does with that clock.
  const firstGuess = wallAsUtc - zoneOffsetMinutes(wallAsUtc, timeZone) * MS_PER_MINUTE;
  const resolved = wallAsUtc - zoneOffsetMinutes(firstGuess, timeZone) * MS_PER_MINUTE;

  return new Date(resolved).toISOString();
}
